// ===========================================
// "Registration fee credit" rows for Create Invoice and the draft Edit (#374 Phase 3)
// ===========================================
// One pre-ticked row per child of THIS DRAFT whose paid registration fee is HELD and who is provably
// on it (an enrolment linked to the invoice: the server's own rule, #374 /qa H1, so the dialog never
// offers a child the server would refuse). A typed invoice has no links and so shows nothing. Ticked
// rows are sent as `addRegistrationCreditStudentIds`; the server re-judges each under its locks, so a
// fee taken meanwhile is simply not applied (and the save says so). Shown only with the school's
// switch ON and `registration_fees.write` (the credit is not a pricing decision, D15). Nothing is
// rendered while loading, on error or with nothing held: the credit is an offer, never a gate.

import { useEffect, useRef } from 'react';
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { useHeldRegistrationCredits } from '../../hooks/useRegistrationFeeSettings';
import { formatCurrencyFromDollars } from '../../utils/currency';

interface RegistrationCreditSectionProps {
  familyId: string | undefined;
  invoiceId: string | undefined;
  selectedIds: string[];
  onChange: (studentIds: string[]) => void;
}

export default function RegistrationCreditSection({ familyId, invoiceId, selectedIds, onChange }: RegistrationCreditSectionProps) {
  const { can } = usePermissions();
  // The read route needs registration_fees.read; crediting needs .write (#374 /qa L4, #272).
  const mayCredit = can('registration_fees', 'write');
  const mayRead = can('registration_fees', 'read');
  const { data } = useHeldRegistrationCredits(familyId, invoiceId, mayCredit && mayRead);
  // Pre-ticked once per invoice: ticking a row off must stick through refetches.
  const initialisedFor = useRef<string | null>(null);

  const children = data && data.creditOnFirstInvoice ? data.children : [];
  useEffect(() => {
    if (!familyId || !invoiceId || !data || initialisedFor.current === invoiceId) return;
    initialisedFor.current = invoiceId;
    onChange(children.map((c) => c.studentId));
    // `onChange` is a state setter from the parent; the effect must run once per family and data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, invoiceId, data]);

  if (!mayCredit || !mayRead || children.length === 0) return null;

  return (
    <Box data-testid="registration-credit-section">
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Registration fee credit
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        A paid registration fee comes off the child&apos;s tuition. It is a credit on this invoice, not a refund.
      </Typography>
      <FormGroup>
        {children.map((child) => (
          <FormControlLabel
            key={child.registrationPaymentId}
            control={
              <Checkbox
                size="small"
                checked={selectedIds.includes(child.studentId)}
                onChange={(_, checked) =>
                  onChange(checked ? [...selectedIds, child.studentId] : selectedIds.filter((id) => id !== child.studentId))
                }
              />
            }
            label={`Registration fee credit, ${child.studentName}, ${formatCurrencyFromDollars(child.amount)}`}
          />
        ))}
      </FormGroup>
    </Box>
  );
}
